import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface CompanySupplier {
  id: number;
  company_id: number;
  name: string;
  email: string;
  phone: string;
  wechat: string | null;
  country: string;
  province: string;
  city: string;
  address: string;
}

export interface CompanySupplierInput {
  company_id: number;
  name: string;
  email: string;
  phone: string;
  wechat?: string | null;
  country: string;
  province: string;
  city: string;
  address: string;
}

export const useCompanySuppliers = (companyId?: number | null) => {
  const [suppliers, setSuppliers] = useState<CompanySupplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('company_supplier')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setSuppliers(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch company suppliers');
        console.error('Error fetching company suppliers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, [companyId]);

  const saveCompanySupplier = async (supplier: CompanySupplierInput) => {
    try {
      const { data, error } = await supabase
        .from('company_supplier')
        .insert(supplier)
        .select()
        .single();

      if (error) throw error;
      setSuppliers(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error saving company supplier:', err);
      throw err;
    }
  };

  const updateCompanySupplier = async (id: number, updates: Partial<CompanySupplierInput>) => {
    try {
      const { data, error } = await supabase
        .from('company_supplier')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      setSuppliers(prev => 
        prev.map(supplier => supplier.id === id ? data : supplier)
      );
      
      return data;
    } catch (err) {
      console.error('Error updating company supplier:', err);
      throw err;
    }
  };

  const deleteCompanySupplier = async (id: number) => {
    try {
      const { error } = await supabase
        .from('company_supplier')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setSuppliers(prev => prev.filter(supplier => supplier.id !== id));
      
      return true;
    } catch (err) {
      console.error('Error deleting company supplier:', err);
      throw err;
    }
  };

  return { 
    suppliers, 
    loading, 
    error, 
    saveCompanySupplier, 
    updateCompanySupplier,
    deleteCompanySupplier
  };
}; 